import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  ShieldCheck, LogIn, LogOut, KeyRound, XCircle, RefreshCw, Search, Download, Globe2, MapPin,
} from "lucide-react";

const EVENT_META = {
  login_success:            { icon: LogIn,       tone: "text-emerald-700 bg-emerald-100 border-emerald-400/25", label: "Login ✓" },
  login_failed:             { icon: XCircle,     tone: "text-red-700 bg-red-100 border-red-400/25",             label: "Login ✗" },
  logout:                   { icon: LogOut,      tone: "text-blue-700 bg-blue-100 border-blue-400/25",           label: "Logout" },
  password_changed:         { icon: KeyRound,    tone: "text-amber-700 bg-amber-100 border-amber-400/25",       label: "Password changed" },
  password_reset:           { icon: KeyRound,    tone: "text-purple-700 bg-purple-100 border-purple-400/25",   label: "Password reset (email)" },
  sessions_revoked_others:  { icon: ShieldCheck, tone: "text-orange-600 bg-orange-400/10 border-orange-400/25",   label: "Signed out other devices" },
};

export default function AdminAuthEvents() {
  const [rows, setRows] = useState([]);
  const [known, setKnown] = useState([]);
  const [q, setQ] = useState("");
  const [event, setEvent] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 300 };
      if (event !== "all") params.event = event;
      const r = await api.get("/admin/security-events", { params });
      setRows(r.data.events || []);
      setKnown(r.data.known_events || []);
    } catch { /* silent */ }
    setLoading(false);
  }, [event]);
  useEffect(() => { load(); }, [load]);

  const filtered = rows.filter((r) => {
    if (!q) return true;
    const hay = `${r.email || ""} ${r.event || ""} ${r.ip || ""} ${r.user_agent || ""} ${JSON.stringify(r.meta || {})}`.toLowerCase();
    return hay.includes(q.toLowerCase());
  });

  const exportCsv = () => {
    const header = ["When", "Event", "Email", "Role", "IP", "Country", "User-Agent", "Meta"];
    const lines = [header.join(",")];
    filtered.forEach((r) => {
      lines.push([
        (r.at || "").replace("T", " ").slice(0, 19),
        r.event || "", r.email || "", r.role || "", r.ip || "",
        r.cf_country || "", (r.user_agent || "").slice(0, 120),
        JSON.stringify(r.meta || {}).replace(/"/g, '""'),
      ].map((v) => `"${v}"`).join(","));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `zenith360-auth-events-${Date.now()}.csv`;
    a.click();
  };

  return (
    <div className="p-6 space-y-6 text-foreground" data-testid="admin-auth-events-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-orange-600">// Security</div>
          <h1 className="text-3xl font-bold mt-1">Auth event log</h1>
          <div className="text-muted-foreground text-sm mt-1 max-w-2xl">
            Every login, logout, password change, reset, and session revocation captured with
            IP, user-agent, and Cloudflare country. Retained forever — investigate suspicious
            activity in seconds.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground/80" />
            <Input value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search email / IP / UA…"
              className="h-9 rounded-sm bg-background text-foreground border-border pl-8 text-sm w-64"
              data-testid="auth-events-search" />
          </div>
          <Select value={event} onValueChange={setEvent}>
            <SelectTrigger className="h-9 w-56 rounded-sm bg-background text-foreground border-border"
              data-testid="auth-events-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Every event</SelectItem>
              {known.map((e) => (
                <SelectItem key={e} value={e}>{EVENT_META[e]?.label || e}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={load}
            className="rounded-sm h-9 border-border bg-card hover:bg-muted text-foreground"
            data-testid="auth-events-refresh">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          </Button>
          <Button variant="outline" onClick={exportCsv}
            className="rounded-sm h-9 border-border bg-card hover:bg-muted text-foreground gap-1.5"
            data-testid="auth-events-export">
            <Download className="w-3.5 h-3.5" /> CSV
          </Button>
        </div>
      </div>

      <div className="space-y-2">
        {loading && <div className="text-muted-foreground text-sm">Loading events…</div>}
        {!loading && filtered.length === 0 && (
          <Card className="rounded-sm bg-card border-border p-10 text-center text-muted-foreground">
            <ShieldCheck className="w-10 h-10 mx-auto opacity-30" />
            <div className="mt-3">No events match this filter yet.</div>
            <div className="text-xs mt-1">Sign in or out — the row will appear here instantly.</div>
          </Card>
        )}
        {filtered.map((r) => {
          const meta = EVENT_META[r.event] || { icon: ShieldCheck, tone: "text-foreground/80 bg-card border-border", label: r.event };
          const Icon = meta.icon;
          return (
            <Card key={r.id} className={`rounded-sm border p-3 flex items-start gap-3 ${meta.tone}`}
              data-testid={`auth-event-${r.id?.slice(0, 8)}`}>
              <div className="w-9 h-9 rounded-sm bg-muted flex items-center justify-center shrink-0">
                <Icon className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] font-mono uppercase tracking-widest">{meta.label}</span>
                  {r.email && <span className="text-foreground/90 text-sm font-medium">{r.email}</span>}
                  {r.role && <span className="text-[10px] font-mono uppercase text-muted-foreground">· {r.role}</span>}
                </div>
                <div className="flex items-center gap-3 mt-1 text-[11px] text-muted-foreground font-mono">
                  {r.ip && (
                    <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {r.ip}</span>
                  )}
                  {r.cf_country && (
                    <span className="flex items-center gap-1"><Globe2 className="w-3 h-3" /> {r.cf_country}</span>
                  )}
                  {r.user_agent && (
                    <span className="truncate max-w-[380px]" title={r.user_agent}>{r.user_agent}</span>
                  )}
                </div>
                {r.meta && Object.keys(r.meta).length > 0 && (
                  <div className="text-[11px] text-muted-foreground font-mono mt-1 break-all">
                    {Object.entries(r.meta).slice(0, 5).map(([k, v]) => (
                      <span key={k} className="mr-3">
                        <span className="text-muted-foreground/80">{k}:</span> {typeof v === "object" ? JSON.stringify(v) : String(v)}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div className="text-[10px] font-mono text-muted-foreground/80 whitespace-nowrap shrink-0">
                {r.at ? new Date(r.at).toLocaleString() : "—"}
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
